/**
 * QuickViewDrawer — drawer lateral genérico para entidades relacionadas de la
 * Vista 360 (cliente, pedido, comprobante, producto, pago, factoring).
 *
 * Overlay + slide-in desde la derecha; cierra por ✕, backdrop o Esc. Responsive
 * (92vw en móvil). Controlado: `open`/`onClose`. El contenido (header, acciones,
 * body) lo provee el consumidor; el cableado a datos reales es Fase 3.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

const ANIM_MS = 260

export type QuickViewType = 'customer' | 'order' | 'voucher' | 'product' | 'payment' | 'factoring'

interface QuickViewDrawerProps {
  open: boolean
  onClose: () => void
  /** Ícono del header (ej: <User size={19} />). */
  icon?: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  /** Barra de acciones contextuales (botones). Se oculta si está vacía. */
  actions?: ReactNode
  children: ReactNode
}

export function QuickViewDrawer({
  open,
  onClose,
  icon,
  title,
  subtitle,
  actions,
  children,
}: QuickViewDrawerProps) {
  // El componente se monta ya con open=true, así que necesitamos un estado de
  // "entrada" que arranque cerrado y pase a abierto en el frame siguiente para
  // que la transición de slide+fade se reproduzca (si no, aparece de golpe).
  // `requestClose` reproduce la salida antes de desmontar (el padre desmonta vía onClose).
  const [entered, setEntered] = useState(false)
  const requestClose = () => {
    setEntered(false)
    window.setTimeout(onClose, ANIM_MS)
  }

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  // Cierre por Esc + bloqueo de scroll del body mientras está abierto.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return createPortal(
    <div
      aria-hidden={!entered}
      onClick={(e) => {
        if (e.target === e.currentTarget) requestClose()
      }}
      className={[
        'fixed inset-0 z-[100] bg-black/45 transition-opacity ease-out',
        entered ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
      ].join(' ')}
      style={{ transitionDuration: `${ANIM_MS}ms` }}
    >
      <aside
        role="dialog"
        aria-modal="true"
        className={[
          'fixed top-0 right-0 h-screen w-[480px] max-w-[92vw] bg-surface flex flex-col',
          'shadow-[-10px_0_36px_rgba(8,12,20,0.28)] transition-transform ease-out will-change-transform',
          entered ? 'translate-x-0' : 'translate-x-full',
        ].join(' ')}
        style={{ transitionDuration: `${ANIM_MS}ms`, transitionTimingFunction: 'cubic-bezier(.4,0,.2,1)' }}
      >
        <header className="flex items-start gap-3 px-[18px] py-4 border-b border-divider shrink-0">
          {icon && (
            <div className="w-[42px] h-[42px] rounded-xl grid place-items-center shrink-0 bg-soft text-primary">
              {icon}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[17px] font-extrabold -tracking-[0.2px] text-content truncate">
              {title}
            </div>
            {subtitle && <div className="text-[12.5px] text-content-muted mt-0.5">{subtitle}</div>}
          </div>
          <button
            type="button"
            onClick={requestClose}
            title="Cerrar"
            className="ml-auto w-8 h-8 rounded-lg border border-divider bg-surface text-content-muted grid place-items-center shrink-0 hover:bg-surface-muted hover:text-content"
          >
            <X size={16} />
          </button>
        </header>

        {actions && (
          <div className="flex gap-2 flex-wrap px-[18px] py-3 border-b border-divider bg-surface-muted shrink-0">
            {actions}
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-slim p-[18px]">{children}</div>
      </aside>
    </div>,
    document.body,
  )
}
